import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { SocketProvider } from './context/SocketContext';
import { ShortcutsProvider, useShortcutsContext } from './context/ShortcutsContext';
import { PermissionProvider } from './context/PermissionContext';
import { LocationProvider } from './context/LocationContext';
import { CircularProgress, Box } from '@mui/material';

import Layout from './components/Layout';
import Home from './pages/Home';
import ContactForm from './pages/ContactForm';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Sales from './pages/Sales';
import SaleDetail from './pages/SaleDetail';
import Inventory from './pages/Inventory';
import Calendar from './pages/Calendar';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import Clients from './pages/Clients';
import Invoices from './pages/Invoices';
import Payments from './pages/Payments';
import Commissions from './pages/Commissions';
import Documents from './pages/Documents';
import Settings from './pages/Settings';
import Users from './pages/Users';
import Analytics from './pages/Analytics';
import Profile from './pages/Profile';
import ContactRequests from './pages/ContactRequests';
import Appointments from './pages/Appointments';
import StaffSchedule from './pages/StaffSchedule';
import EmailTemplates from './pages/EmailTemplates';
import Notifications from './pages/Notifications';
import Offline from './pages/Offline';
import Help from './pages/Help';
import HelpArticle from './pages/HelpArticle';
import DataExport from './pages/DataExport';
import Backup from './pages/Backup';
import Roles from './pages/Roles';
import RoleDetail from './pages/RoleDetail';
import Locations from './pages/Locations';
import LocationDetail from './pages/LocationDetail';
import Consignments from './pages/Consignments';
import ConsignmentDetail from './pages/ConsignmentDetail';
import VendorPayouts from './pages/VendorPayouts';
import APIDocs from './pages/APIDocs';
import Auctions from './pages/Auctions';
import AuctionDetail from './pages/AuctionDetail';
import AuctionAdmin from './pages/AuctionAdmin';
import Marketing from './pages/Marketing';

// Vendor Portal
import { VendorProvider, useVendor as useVendorAuth } from './vendor/VendorContext';
import VendorLogin from './vendor/VendorLogin';
import VendorLayout from './vendor/VendorLayout';
import VendorDashboard from './vendor/VendorDashboard';
import VendorSales from './vendor/VendorSales';
import VendorSaleDetail from './vendor/VendorSaleDetail';
import VendorConsignments from './vendor/VendorConsignments';
import VendorConsignmentDetail from './vendor/VendorConsignmentDetail';
import VendorPayoutsPortal from './vendor/VendorPayouts';
import VendorPayoutDetail from './vendor/VendorPayoutDetail';
import VendorDocuments from './vendor/VendorDocuments';
import VendorStatements from './vendor/VendorStatements';
import VendorProfile from './vendor/VendorProfile';

// Contract pages
import Contracts from './pages/Contracts';
import ContractDetail from './pages/ContractDetail';
import ContractTemplates from './pages/ContractTemplates';
import ContractSign from './pages/ContractSign';

// Public pages
import PublicHome from './pages/public/PublicHome';
import PublicSales from './pages/public/PublicSales';
import PublicSaleDetail from './pages/public/PublicSaleDetail';

// Customer Portal
import { PortalProvider, usePortal } from './portal/PortalContext';
import PortalLayout from './portal/PortalLayout';
import PortalLogin from './portal/PortalLogin';
import PortalRegister from './portal/PortalRegister';
import PortalDashboard from './portal/PortalDashboard';
import PortalSales from './portal/PortalSales';
import PortalSaleDetail from './portal/PortalSaleDetail';
import PortalPurchases from './portal/PortalPurchases';
import PortalPurchaseDetail from './portal/PortalPurchaseDetail';
import PortalWishlist from './portal/PortalWishlist';
import PortalProfile from './portal/PortalProfile';

// PWA Components
import InstallPrompt from './components/InstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import UpdatePrompt from './components/UpdatePrompt';
import OnboardingTour from './components/OnboardingTour';

// Keyboard Shortcuts Components
import ShortcutsDialog from './components/ShortcutsDialog';
import CommandPalette from './components/CommandPalette';
import ShortcutToastProvider from './components/ShortcutToast';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const VendorProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useVendorAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/vendor/login" replace />;
  }

  return children;
};

const PortalProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = usePortal();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }

  return children;
};

const PortalPublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = usePortal();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/portal/dashboard" replace />;
  }

  return children;
};

// Shortcuts Manager Component
const ShortcutsManager = () => {
  const { showDialog, showCommandPalette, setShowDialog, setShowCommandPalette } = useShortcutsContext();

  return (
    <>
      <ShortcutsDialog open={showDialog} onClose={() => setShowDialog(false)} />
      <CommandPalette open={showCommandPalette} onClose={() => setShowCommandPalette(false)} />
    </>
  );
};

function App() {
  const [swRegistration, setSwRegistration] = useState(null);

  useEffect(() => {
    // Listen for service worker updates
    const handleSwUpdate = (event) => {
      setSwRegistration(event.detail);
    };

    window.addEventListener('swUpdate', handleSwUpdate);

    return () => {
      window.removeEventListener('swUpdate', handleSwUpdate);
    };
  }, []);

  return (
    <NotificationProvider>
      <SocketProvider>
        <LocationProvider>
          <PermissionProvider>
            <VendorProvider>
              <PortalProvider>
                <ShortcutsProvider>
                  <ShortcutToastProvider>
                    {/* PWA Components */}
                    <InstallPrompt />
                    <OfflineIndicator />
                    <UpdatePrompt registration={swRegistration} />
                    <OnboardingTour />

                    {/* Keyboard Shortcuts Components */}
                    <ShortcutsManager />

                    <Routes>
        {/* Public Routes - No authentication required */}
        <Route path="/" element={<PublicHome />} />
        <Route path="/public/sales" element={<PublicSales />} />
        <Route path="/public/sales/:id" element={<PublicSaleDetail />} />
        <Route path="/contact" element={<ContactForm />} />
        <Route path="/offline" element={<Offline />} />

        {/* Public Contract Signing Route */}
        <Route path="/contracts/sign/:token" element={<ContractSign />} />

        {/* Old home route for backwards compatibility */}
        <Route path="/home" element={<Home />} />

        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />

        {/* Staff Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
      <Route
        path="/staff/sales"
        element={
          <ProtectedRoute>
            <Layout>
              <Sales />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff/sales/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <SaleDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Legacy sales routes for backwards compatibility */}
      <Route path="/sales" element={<Navigate to="/staff/sales" replace />} />
      <Route path="/sales/:id" element={<Navigate to="/staff/sales/:id" replace />} />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <Layout>
              <Inventory />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/auctions"
        element={
          <ProtectedRoute>
            <Layout>
              <Auctions />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/auctions/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <AuctionDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/auctions"
        element={
          <ProtectedRoute adminOnly={true}>
            <Layout>
              <AuctionAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/consignments"
        element={
          <ProtectedRoute>
            <Layout>
              <Consignments />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/consignments/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ConsignmentDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-payouts"
        element={
          <ProtectedRoute>
            <Layout>
              <VendorPayouts />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <Layout>
              <Calendar />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            <Layout>
              <Appointments />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff-schedule"
        element={
          <ProtectedRoute>
            <Layout>
              <StaffSchedule />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <Layout>
              <Leads />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <LeadDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <Layout>
              <Clients />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <Layout>
              <Invoices />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <Layout>
              <Payments />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/commissions"
        element={
          <ProtectedRoute>
            <Layout>
              <Commissions />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <Layout>
              <Documents />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing"
        element={
          <ProtectedRoute>
            <Layout>
              <Marketing />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contracts"
        element={
          <ProtectedRoute>
            <Layout>
              <Contracts />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contracts/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ContractDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contracts/templates"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <ContractTemplates />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-docs"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <APIDocs />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/export"
        element={
          <ProtectedRoute>
            <Layout>
              <DataExport />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/backup"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <Backup />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <Users />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <Analytics />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <Analytics />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contact-requests"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <ContactRequests />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-templates"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <EmailTemplates />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <Profile />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Layout>
              <Notifications />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <Layout>
              <Help />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/help/article/:articleId"
        element={
          <ProtectedRoute>
            <Layout>
              <HelpArticle />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/help/category/:categoryId"
        element={
          <ProtectedRoute>
            <Layout>
              <Help />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/roles"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <Roles />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/roles/:id"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <RoleDetail />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/locations"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <Locations />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations/:id"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <LocationDetail />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Customer Portal Routes */}
      <Route
        path="/portal/login"
        element={
          <PortalPublicRoute>
            <PortalLayout>
              <PortalLogin />
            </PortalLayout>
          </PortalPublicRoute>
        }
      />
      <Route
        path="/portal/register"
        element={
          <PortalPublicRoute>
            <PortalLayout>
              <PortalRegister />
            </PortalLayout>
          </PortalPublicRoute>
        }
      />
      <Route
        path="/portal/sales"
        element={
          <PortalLayout>
            <PortalSales />
          </PortalLayout>
        }
      />
      <Route
        path="/portal/sales/:id"
        element={
          <PortalLayout>
            <PortalSaleDetail />
          </PortalLayout>
        }
      />
      <Route
        path="/portal/dashboard"
        element={
          <PortalProtectedRoute>
            <PortalLayout>
              <PortalDashboard />
            </PortalLayout>
          </PortalProtectedRoute>
        }
      />
      <Route
        path="/portal/purchases"
        element={
          <PortalProtectedRoute>
            <PortalLayout>
              <PortalPurchases />
            </PortalLayout>
          </PortalProtectedRoute>
        }
      />
      <Route
        path="/portal/purchases/:id"
        element={
          <PortalProtectedRoute>
            <PortalLayout>
              <PortalPurchaseDetail />
            </PortalLayout>
          </PortalProtectedRoute>
        }
      />
      <Route
        path="/portal/wishlist"
        element={
          <PortalProtectedRoute>
            <PortalLayout>
              <PortalWishlist />
            </PortalLayout>
          </PortalProtectedRoute>
        }
      />
      <Route
        path="/portal/profile"
        element={
          <PortalProtectedRoute>
            <PortalLayout>
              <PortalProfile />
            </PortalLayout>
          </PortalProtectedRoute>
        }
      />

      {/* Vendor Portal Routes */}
      <Route path="/vendor/login" element={<VendorLogin />} />
      <Route
        path="/vendor/dashboard"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorDashboard />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/sales"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorSales />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/sales/:id"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorSaleDetail />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/consignments"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorConsignments />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/consignments/:id"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorConsignmentDetail />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/payouts"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorPayoutsPortal />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/payouts/:id"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorPayoutDetail />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/documents"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorDocuments />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/statements"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorStatements />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/profile"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorProfile />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </ShortcutToastProvider>
                </ShortcutsProvider>
              </PortalProvider>
            </VendorProvider>
          </PermissionProvider>
        </LocationProvider>
      </SocketProvider>
    </NotificationProvider>
  );
}

export default App;
